import { SYNC_OPERATION_STATUSES } from "../../../domain/valueObjects/syncOperationStatuses";
import { nowIso } from "../../../domain/utils/timestamps";

export class BackendAgnosticSyncEngine {
  constructor({ syncOperationRepository, handlers }) {
    this.syncOperationRepository = syncOperationRepository;
    this.handlers = handlers;
  }

  async runPending({ limit = 50 } = {}) {
    const pending = await this.syncOperationRepository.listByStatus(
      SYNC_OPERATION_STATUSES.PENDING
    );
    const failed = await this.syncOperationRepository.listByStatus(
      SYNC_OPERATION_STATUSES.FAILED
    );

    const operations = [...pending, ...failed]
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .slice(0, limit);

    const results = [];

    for (const operation of operations) {
      results.push(await this.runOperation(operation));
    }

    return results;
  }

  async runOperation(operation) {
    const handler = this.handlers[operation.entityType];

    if (!handler) {
      const failedOperation = {
        ...operation,
        status: SYNC_OPERATION_STATUSES.FAILED,
        attempts: (operation.attempts || 0) + 1,
        updatedAt: nowIso(),
        lastError: `No sync handler registered for ${operation.entityType}`,
      };
      await this.syncOperationRepository.put(failedOperation);
      return { operation: failedOperation, success: false };
    }

    const inProgressOperation = {
      ...operation,
      status: SYNC_OPERATION_STATUSES.IN_PROGRESS,
      updatedAt: nowIso(),
    };
    await this.syncOperationRepository.put(inProgressOperation);

    try {
      await handler.sync(inProgressOperation);

      const succeededOperation = {
        ...inProgressOperation,
        status: SYNC_OPERATION_STATUSES.SUCCEEDED,
        updatedAt: nowIso(),
        lastError: undefined,
      };
      await this.syncOperationRepository.put(succeededOperation);
      return { operation: succeededOperation, success: true };
    } catch (error) {
      const failedOperation = {
        ...inProgressOperation,
        status: SYNC_OPERATION_STATUSES.FAILED,
        attempts: (inProgressOperation.attempts || 0) + 1,
        updatedAt: nowIso(),
        lastError: error.message || "Unknown sync error",
      };
      await this.syncOperationRepository.put(failedOperation);
      return { operation: failedOperation, success: false, error };
    }
  }
}
